// REACT IMPORTS
import React from "react";
import { BrowserRouter } from "react-router-dom";
import ReactDOM from "react-dom/client";
import reportWebVitals from "./reportWebVitals";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { CookiesProvider } from "react-cookie";

// COMPONENT IMPORTS
import { SkeletonTheme } from "react-loading-skeleton";
import { persistor, store } from "./store/store";
import App from "App";
import SentryErrorBoundary from "components/error/SentryErrorBoundary";
import SentryUserContext from "components/error/SentryUserContext";
import { initSentry } from "utils/sentry";
import { installSafeDateFormatting } from "utils/DateFormat";

// STYLESHEET IMPORTS
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import "react-loading-skeleton/dist/skeleton.css";
import "./index.css";
import "animate.css";

// Last line of defence for customer-facing dates. Without it, any date moment
// cannot parse formats as the literal string "Invalid date" and shows it to the
// customer. Prefer formatPaymentListDate/formatDisplayDate, which also handle a
// missing value — this only stops an unparseable one from surfacing raw.
installSafeDateFormatting();

// Debug logging is useful while developing but should not reach customers'
// browser consoles. Warnings and errors are left intact so real problems and
// Sentry reporting still surface.
if (process.env.NODE_ENV === "production") {
  console.log = () => {};
  console.debug = () => {};
  console.info = () => {};
}

initSentry();

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <CookiesProvider defaultSetOptions={{ path: "/" }}>
        <BrowserRouter>
          <SkeletonTheme
            duration={1}
            borderRadius={6}
            baseColor="var(--color-skeleton-base)"
            highlightColor="var(--color-skeleton-highlight)"
          >
            <SentryUserContext />
            <SentryErrorBoundary>
              <App />
            </SentryErrorBoundary>
          </SkeletonTheme>
        </BrowserRouter>
      </CookiesProvider>
    </PersistGate>
  </Provider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
